import { ExpandLess, KeyboardArrowRight, KeyboardDoubleArrowRight } from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  Paper,
  Theme,
  Toolbar,
  Typography,
  useColorScheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import { clone } from 'ramda';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { trackConnectSelected } from '../../analytics/bpsEvents';
import { readConnectionPreferences } from '../../hooks/storage/useConnectionPreferences';
import usePowerStateIcon from '../../hooks/ui/usePowerStateIcon';
import useUserProgramsSlotLoading from '../../hooks/ui/useUserProgramsSlotLoading';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { projectSlice } from '../../redux/ProjectSlice.ts';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { robotDataSlice, type FirmwareInstallProgress } from '../../redux/RobotDataSlice';
import { robotSlice, selectAreDrivebaseCommandActionsDisabled, selectAreDrivebaseFirmwareActionsDisabled } from '../../redux/RobotSlice';
import { DRIVEBASE_SYSTEM_STATE } from '../../robot/drivebase-system-state';
import { isFirmwareTransferRetryState } from '../../robot/robot-service-helpers';
import { ApplicationActions, DEFAULT_SELECTED_ROBOT, RobotActions } from '../../utils/constants';
import DeleteSlotConfirmDialog from '../Dialogs/Project/DeleteSlotConfirmDialog';
import { BprPortIcon } from '../Icons/Bits';
import WiredIcon from '../Icons/Connection/WiredIcon';
import WirelessIcon from '../Icons/Connection/WirelessIcon';
import AccessoryUpdateStatusPanel from './ConnectedDrawer/AccessoryUpdateStatusPanel';
import ControllerInfoPanel from './ConnectedDrawer/ControllerInfoPanel';
import DriveUnitInfoPanel from './ConnectedDrawer/DriveUnitInfoPanel';
import FirmwareStatusPanel from './ConnectedDrawer/FirmwareStatusPanel';
import SwerveSlotManagementPanel from './ConnectedDrawer/SwerveSlotManagementPanel';
import ElectronicExplorerItem from './ElectronicExplorerItem';
import PortControl from './Electronics/PortControl';

const { CONNECTED_DRAWER } = ApplicationActions.Drawer;
const { CONNECTED, CONNECTION_TYPE } = RobotActions.STATE;
const NON_PORT_REMOTE_DEVICE_SYSTEM_INFO_IDLE_POLL_MS = 3000;
const NON_PORT_REMOTE_DEVICE_SYSTEM_INFO_FIRMWARE_POLL_MS = 1500;
type SectionsExpandedState = {
  driveUnit: boolean;
  controller: boolean;
  electronics: boolean;
  swerveSlots: boolean;
};

const DEFAULT_SECTIONS_EXPANDED: SectionsExpandedState = {
  driveUnit: false,
  controller: false,
  electronics: true,
  swerveSlots: true,
};

const ConnectedDrawer = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { mode, systemMode } = useColorScheme();
  let colorMode = mode;
  if (mode === 'system') {
    colorMode = systemMode;
  }

  const { connectionType } = useAppSelector((state) => state.robot);
  const openDrawer = useAppSelector((state) => state.project.openDrawer);
  const { robotState, runtimeProgramState } = useAppSelector((state) => state.robot);
  const {
    bluetoothAdvertisingName,
    electronics: electronicsFromRedux,
    firmwareInstallProgress,
    robotType,
  } = useAppSelector((state) => state.robotData);

  const firmware = useAppSelector((state) => state.robot.firmware) as {
    status: string | null;
    firmwareState: string | null;
    errorCode: number | null;
    progress: {
      libraryId?: string;
      assetId?: string;
      state?: string;
      percent?: number;
    } | null;
    optionalUpdate: unknown;
  };
  const firmwareStatus = firmware.status;
  const firmwareUpdateState = firmware.firmwareState;
  const currentAssetState = firmware.progress?.state;
  const assetId = firmware.progress?.assetId || firmware.progress?.libraryId;
  const percent = typeof firmware.progress?.percent === 'number' ? firmware.progress.percent : 0;
  const hasFiniteProgressPercent = typeof firmware.progress?.percent === 'number' && Number.isFinite(firmware.progress.percent);
  const hasTransferProgress = Boolean(assetId) || hasFiniteProgressPercent;
  const isTransferRetryAvailable =
    firmwareStatus === RobotActions.FIRMWARE_STATUS.UPDATE_AVAILABLE && isFirmwareTransferRetryState(firmwareUpdateState);
  const isTransferring = firmwareUpdateState === RobotActions.FIRMWARE_UPDATE.STARTED && hasTransferProgress;
  const isPreparingTransfer = firmwareStatus === RobotActions.FIRMWARE_STATUS.UPDATE_AVAILABLE && !isTransferRetryAvailable && !isTransferring;
  const isFinalizing =
    firmwareUpdateState === RobotActions.FIRMWARE_UPDATE.FINALIZE_UPDATES ||
    firmwareUpdateState === RobotActions.FIRMWARE_UPDATE.FINALIZATION_PROGRESS;
  const needsDownload = isTransferRetryAvailable;
  const needsInstall = firmwareStatus === RobotActions.FIRMWARE_STATUS.READY_TO_INSTALL;
  const updatingAccessories = firmwareInstallProgress.filter((p: FirmwareInstallProgress) => Boolean(p?.updating));
  const showAccessoryFirmwareUpdateProgress = updatingAccessories.length > 0;
  const runtimeCommandActionsDisabled = useAppSelector(selectAreDrivebaseCommandActionsDisabled);
  const firmwareActionsDisabled = useAppSelector(selectAreDrivebaseFirmwareActionsDisabled);
  const { hasVisibleSlotPrograms, isUserProgramsLoading } = useUserProgramsSlotLoading();
  const wasUserProgramsLoadingRef = useRef(false);

  const { userRobotColor } = useAppSelector((state) => state.robotData);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [pendingConnectionSwitch, setPendingConnectionSwitch] = useState<string | null>(null);
  const [sectionsExpanded, setSectionsExpanded] = useState<SectionsExpandedState>(DEFAULT_SECTIONS_EXPANDED);
  const toggleSection = (key: keyof typeof sectionsExpanded) => {
    setSectionsExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const electronics = useMemo(() => {
    const arr = Array.isArray(electronicsFromRedux) ? electronicsFromRedux : [];
    const result = arr.map((e) => ({
      electronicType: (e?.electronicType ?? null) as string | null,
      info: (e?.info ?? {}) as {
        devicePartNumber?: string;
        firmwareBootloader?: string;
        firmwareMainApp?: string;
      },
    }));
    while (result.length < 8) {
      result.push({ electronicType: null, info: {} });
    }
    return result.slice(0, 8);
  }, [electronicsFromRedux]);

  const { onOpenDrawer } = projectSlice.actions;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteSlotKey, setPendingDeleteSlotKey] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const requestDeleteSlotProgram = (slotKey: string) => {
    setPendingDeleteSlotKey(slotKey);
    setDeleteConfirmOpen(true);
  };

  const clearDeleteDialog = () => {
    setDeleteConfirmOpen(false);
    setPendingDeleteSlotKey(null);
  };

  const handleDeleteConfirmed = async () => {
    if (pendingDeleteSlotKey === null || isDeleting || runtimeCommandActionsDisabled) return;

    setIsDeleting(true);
    try {
      await window.robotService?.erasePythonProgram?.(pendingDeleteSlotKey);
      await window.robotService?.queryRobotSlotStorage?.();
      clearDeleteDialog();
    } catch (error) {
      console.error('Failed to delete program from Drive Unit slot', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    dispatch(onOpenDrawer(null));
  };

  useEffect(() => {
    if (robotState === CONNECTED) {
      if (openDrawer === CONNECTED_DRAWER) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    } else {
      setOpen(false);
    }
  }, [openDrawer, robotState]);

  useEffect(() => {
    if (robotState === CONNECTED) {
      return;
    }

    setSectionsExpanded(DEFAULT_SECTIONS_EXPANDED);
  }, [robotState]);

  useEffect(() => {
    if (isUserProgramsLoading) {
      setSectionsExpanded((prev) => (prev.swerveSlots ? { ...prev, swerveSlots: false } : prev));
    } else if (wasUserProgramsLoadingRef.current && hasVisibleSlotPrograms) {
      setSectionsExpanded((prev) => ({ ...prev, swerveSlots: true }));
    }

    wasUserProgramsLoadingRef.current = isUserProgramsLoading;
  }, [hasVisibleSlotPrograms, isUserProgramsLoading]);

  // Poll port status via getRemoteDeviceTypes when Electronics Drawer is open (500ms interval)
  useEffect(() => {
    if (robotState !== CONNECTED || openDrawer !== CONNECTED_DRAWER) return;

    const poll = () => window.robotService?.getRemoteDeviceTypes?.();
    poll();
    const id = setInterval(poll, 500);

    return () => {
      clearInterval(id);
    };
  }, [robotState, openDrawer]);

  useEffect(() => {
    if (robotState !== CONNECTED || openDrawer !== CONNECTED_DRAWER) return;

    const nonPortPollMs =
      runtimeProgramState === DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_CORE_FIRMWARE ||
      runtimeProgramState === DRIVEBASE_SYSTEM_STATE.DISTRIBUTING_ACCESSORY_FIRMWARE
        ? NON_PORT_REMOTE_DEVICE_SYSTEM_INFO_FIRMWARE_POLL_MS
        : NON_PORT_REMOTE_DEVICE_SYSTEM_INFO_IDLE_POLL_MS;
    const pollNonPort = () => window.robotService?.getNonPortRemoteDeviceSystemInfo?.();
    pollNonPort();
    const id = setInterval(pollNonPort, nonPortPollMs);

    return () => {
      clearInterval(id);
    };
  }, [robotState, openDrawer, runtimeProgramState]);

  useEffect(() => {
    if (robotState === CONNECTED && openDrawer === CONNECTED_DRAWER) {
      window.robotService?.getRobotFirmwareVersions?.(false, true);
    }
  }, [robotState, openDrawer]);

  const disconnectRobot = () => {
    setPendingConnectionSwitch(null);
    dispatch(projectSlice.actions.onOpenDrawer(null));
    dispatch(robotSlice.actions.onUpdateRobotState(RobotActions.STATE.DISCONNECT));
  };

  const handleSwitchConnection = (nextConnectionType: string) => {
    setPendingConnectionSwitch(nextConnectionType);
    dispatch(projectSlice.actions.onOpenDrawer(null));
    dispatch(robotSlice.actions.onUpdateRobotState(RobotActions.STATE.DISCONNECT));
  };

  useEffect(() => {
    if (pendingConnectionSwitch == null || robotState !== RobotActions.STATE.DISCONNECTED) {
      return;
    }

    const nextConnectionType = pendingConnectionSwitch;
    const connectionPreferences = readConnectionPreferences();

    setPendingConnectionSwitch(null);

    if (nextConnectionType === RobotActions.STATE.CONNECTION_TYPE.BLE) {
      if (connectionPreferences.skipBluetoothInfo) {
        trackConnectSelected(RobotActions.STATE.CONNECTION_TYPE.BLE);
        dispatch(robotDataSlice.actions.onUpdateRobotType(DEFAULT_SELECTED_ROBOT));
        dispatch(
          robotSlice.actions.onUpdateRobotStateAndConnectionType({
            robotState: RobotActions.STATE.CONNECT,
            connectionType: RobotActions.STATE.CONNECTION_TYPE.BLE,
          })
        );
        dispatch(robotDataSlice.actions.onUpdateUserRobotColor(userRobotColor));
        return;
      }

      const openDialog = clone(ApplicationActions.Dialogs.CONNECT_INFO);
      openDialog.options.selectedRobot = DEFAULT_SELECTED_ROBOT.type;
      dispatch(applicationSlice.actions.onOpenDialog(openDialog));
      return;
    }

    if (connectionPreferences.skipUsbInfo) {
      trackConnectSelected(RobotActions.STATE.CONNECTION_TYPE.SERIAL);
      dispatch(robotDataSlice.actions.onUpdateRobotType(DEFAULT_SELECTED_ROBOT));
      dispatch(
        robotSlice.actions.onUpdateRobotStateAndConnectionType({
          robotState: RobotActions.STATE.CONNECT,
          connectionType: RobotActions.STATE.CONNECTION_TYPE.SERIAL,
        })
      );
      dispatch(robotDataSlice.actions.onUpdateUserRobotColor(userRobotColor));
      return;
    }

    dispatch(applicationSlice.actions.onOpenDialog(clone(ApplicationActions.Dialogs.CONNECT_SERIAL)));
  }, [dispatch, pendingConnectionSwitch, robotState, userRobotColor]);

  const openFirmwareStatusDialog = () => {
    const { dialog, options } = ApplicationActions.Dialogs.FIRMWARE_STATUS;
    const firmwareDialog = {
      dialog,
      options: { ...options, robotTypeName: robotType.name },
    };
    dispatch(applicationSlice.actions.onOpenDialog(firmwareDialog));
  };

  const handleFirmwareInstall = () => {
    if (firmwareActionsDisabled) return;
    openFirmwareStatusDialog();
  };

  const handleFirmwareDownload = () => {
    if (firmwareActionsDisabled) return;
    dispatch(robotSlice.actions.onFirmwareStateChange({ firmwareState: RobotActions.FIRMWARE_UPDATE.START_DOWNLOAD }));
  };

  const powerStateIcon = usePowerStateIcon(colorMode === 'dark' ? 'light' : 'dark');

  const drawerSx = {
    flexShrink: 0,
    zIndex: (theme: Theme) => theme.zIndex.drawer + 2,
    '& .MuiDrawer-paper': {
      width: isSmallScreen ? '100%' : isMediumScreen ? '600px' : '750px',
      boxSizing: 'border-box',
      bottom: 0,
      top: 'unset',
      bgcolor: 'background.paper',
      overflow: 'hidden',
    },
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={handleClose} sx={drawerSx}>
        <Toolbar />
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {/* Drawer Title Section */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={1.75}
            position="relative"
            sx={{
              width: '100%',
              flexShrink: 0,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '95%',
                height: '2px',
                backgroundColor: 'accent.main',
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="sectionTitle">
                <Trans i18nKey="connection.title">Connection</Trans>
              </Typography>
            </Box>
            <IconButton onClick={handleClose} aria-label={t('aria-label.close', 'close')} sx={{ position: 'absolute', right: 8 }}>
              <KeyboardDoubleArrowRight />
            </IconButton>
          </Box>

          {/* Scrollable Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              px: 2,
            }}
          >
            {/* Connection Section */}
            <Box width="100%" maxWidth="92%" marginTop={isSmallScreen ? '8px' : '17px'}>
              <Paper elevation={2}>
                <Box padding={isSmallScreen ? '16px' : '24px'}>
                  <Stack direction="row" spacing={isSmallScreen ? 1 : 2} justifyContent="center" alignItems="center" flexWrap="wrap" useFlexGap>
                    {powerStateIcon}
                    {connectionType === CONNECTION_TYPE.BLE ? (
                      <WirelessIcon sx={{ width: '30px', height: '30px' }} />
                    ) : connectionType === CONNECTION_TYPE.SERIAL ? (
                      <WiredIcon sx={{ width: '30px', height: '30px' }} />
                    ) : null}
                    <Typography variant="h1" sx={{ fontSize: isSmallScreen ? '16px' : '24px' }}>
                      {bluetoothAdvertisingName || ''}
                    </Typography>
                  </Stack>

                  <Stack direction="column" spacing={2} justifyContent="center" alignItems="center" marginTop={isSmallScreen ? '16px' : '32px'}>
                    <Button size="large" variant="contained" onClick={disconnectRobot}>
                      <Trans i18nKey="connectedDrawer.button.disconnect">Disconnect Drive Unit</Trans>
                    </Button>
                    {connectionType === CONNECTION_TYPE.BLE ? (
                      <Button size="large" variant="contained" onClick={() => handleSwitchConnection(CONNECTION_TYPE.SERIAL)}>
                        <Trans i18nKey="connectedDrawer.button.connect-with-usb">Connect with USB</Trans>
                      </Button>
                    ) : (
                      <Button size="large" variant="contained" onClick={() => handleSwitchConnection(CONNECTION_TYPE.BLE)}>
                        <Trans i18nKey="connectedDrawer.button.connect-with-bluetooth">Connect with Bluetooth</Trans>
                      </Button>
                    )}
                    <Stack spacing={2} pt={2} sx={{ width: '100%', alignItems: 'center' }}>
                      <FirmwareStatusPanel
                        isPreparingTransfer={isPreparingTransfer}
                        isTransferring={isTransferring}
                        isFinalizing={isFinalizing}
                        needsDownload={needsDownload}
                        needsInstall={needsInstall}
                        assetId={assetId}
                        currentAssetState={currentAssetState}
                        percent={percent}
                        downloadDisabled={firmwareActionsDisabled}
                        installDisabled={firmwareActionsDisabled}
                        onDownload={handleFirmwareDownload}
                        onInstall={handleFirmwareInstall}
                      />
                      {showAccessoryFirmwareUpdateProgress && (
                        <Box sx={{ width: '100%', maxWidth: 360 }}>
                          <AccessoryUpdateStatusPanel progressByDevice={updatingAccessories} />
                        </Box>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Box width="100%" maxWidth="92%" marginTop="17px" marginBottom="17px">
              {/* Electronics Explorer */}
              <Paper elevation={2} sx={{ marginTop: '17px' }}>
                <Box
                  onClick={() => toggleSection('electronics')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 24px',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      component={BprPortIcon}
                      aria-hidden
                      sx={{
                        width: 22,
                        height: 22,
                        display: 'block',
                        color: 'text.primary',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="subHeaderTitle" sx={{ fontSize: isSmallScreen ? '16px' : isMediumScreen ? '18px' : '20px' }}>
                      <Trans i18nKey="connectedDrawer.electronic-explorer.title">Electronic Explorer</Trans>
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    aria-label={sectionsExpanded.electronics ? t('aria-label.collapse', 'collapse') : t('aria-label.expand', 'expand')}
                  >
                    {sectionsExpanded.electronics ? <ExpandLess /> : <KeyboardArrowRight />}
                  </IconButton>
                </Box>
                <Collapse in={sectionsExpanded.electronics}>
                  <Box padding="24px" paddingTop={0}>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <PortControl portStatus={electronics.map((e) => e.electronicType != null)} />
                    </Box>
                    <List sx={{ p: 0, mt: 2 }}>
                      {electronics.map((electronic, index) => (
                        <ElectronicExplorerItem key={index} electronic={electronic} index={index} disableActions={runtimeCommandActionsDisabled} />
                      ))}
                    </List>
                  </Box>
                </Collapse>
              </Paper>

              <SwerveSlotManagementPanel
                isProgramRunning={runtimeCommandActionsDisabled}
                isUserProgramsLoading={isUserProgramsLoading}
                onDelete={requestDeleteSlotProgram}
                expanded={sectionsExpanded.swerveSlots}
                onToggle={() => toggleSection('swerveSlots')}
              />

              <DriveUnitInfoPanel colorMode={colorMode} expanded={sectionsExpanded.driveUnit} onToggle={() => toggleSection('driveUnit')} />

              <ControllerInfoPanel colorMode={colorMode} expanded={sectionsExpanded.controller} onToggle={() => toggleSection('controller')} />
            </Box>
          </Box>
        </Box>
      </Drawer>

      <DeleteSlotConfirmDialog
        open={deleteConfirmOpen}
        isDeleting={isDeleting}
        confirmDisabled={runtimeCommandActionsDisabled}
        onCancel={clearDeleteDialog}
        onConfirm={handleDeleteConfirmed}
      />
    </>
  );
};

export default ConnectedDrawer;
